import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  ChevronDown,
  Heart,
  List,
  Map,
  Menu,
  MessageCircle,
  PawPrint,
  Search,
  SlidersHorizontal,
  Star,
  UserRound,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MarketplaceMapCanvas, { type MarketplaceMapItem } from "@/components/marketplace/MarketplaceMapCanvas";
import MarketplaceInlineSearchBar, { type MarketplaceInlineSearchValue } from "@/components/marketplace/MarketplaceInlineSearchBar";
import { useBuscar } from "@/lib/useBuscar";
import VetQuickRequestDialog, { type VetListing } from "@/components/VetQuickRequestDialog";

const serviceSlugToId: Record<string, number> = {
  hospedagem: 1,
  creche: 2,
  passeio: 3,
  petsitter: 4,
  taxi_dog: 5,
  adestramento: 6,
  veterinario_casa: 7,
  banho_e_tosa: 8,
};

const BATH_GROOMING_SEARCH_OPTIONS = [
  { key: "banho", label: "Só banho" },
  { key: "tosa", label: "Só tosa" },
  { key: "banho_e_tosa", label: "Banho e tosa" },
] as const;

function toYmd(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function money(value?: number | null) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function resultImage(item: any) {
  const image = item?.imagens?.[0];
  if (!image) return "/assets/img/placeholder.png";
  if (String(image).startsWith("http")) return image;
  return String(image).startsWith("/") ? image : `/${image}`;
}

export default function MarketplaceSearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<"list" | "map">(searchParams.get("view") === "map" ? "map" : "list");
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null);
  const [visibleCount, setVisibleCount] = useState(12);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [vetModalidade, setVetModalidade] = useState("todas");
  const [vetEspecialidade, setVetEspecialidade] = useState("");
  const [vetRequestItem, setVetRequestItem] = useState<VetListing | null>(null);

  const city = searchParams.get("cidade") || "";
  const service = searchParams.get("service") || "hospedagem";
  const start = searchParams.get("entrada") || toYmd(new Date());
  const end = searchParams.get("saida") || start;
  const pets = searchParams.get("quantidade_pets") || "1";

  const searchValue: MarketplaceInlineSearchValue = {
    service,
    city,
    startDate: start,
    endDate: end,
    petCount: pets,
  };

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    qs.set("tipo_servico_id", String(serviceSlugToId[service] || 1));
    qs.set("entrada", start);
    qs.set("saida", end);
    if (city) qs.set("cidade", city.split(",")[0].trim());
    if (pets) qs.set("quantidade_pets", pets);
    return qs.toString();
  }, [city, end, pets, service, start]);

  const { items, total, err, loading } = useBuscar(queryString);
  const isVeterinario = serviceSlugToId[service] === 7;
  const vetEspecialidades = useMemo(() => Array.from(new Set((items || []).flatMap((item: any) => Array.isArray(item.veterinario?.especialidades) ? item.veterinario.especialidades : [])))
    .map(String).filter(Boolean).sort((a, b) => a.localeCompare(b, "pt-BR")), [items]);
  const filteredItems = useMemo(() => {
    if (!isVeterinario) return items || [];
    return (items || []).filter((item: any) => {
      const vet = item.veterinario;
      if (vetModalidade !== "todas" && !vet?.[vetModalidade]) return false;
      if (vetEspecialidade && !(vet?.especialidades || []).includes(vetEspecialidade)) return false;
      return true;
    });
  }, [items, isVeterinario, vetModalidade, vetEspecialidade]);

  useEffect(() => {
    setVisibleCount(12);
  }, [queryString, vetModalidade, vetEspecialidade]);

  const listings = useMemo(() => filteredItems.slice(0, visibleCount), [filteredItems, visibleCount]);
  const availableCities = useMemo(
    () => Array.from(new Set((items || []).map((item: any) => item.anfitriao?.cidade).filter(Boolean))).sort((a: string, b: string) => a.localeCompare(b, "pt-BR")),
    [items]
  );

  const mapItems: MarketplaceMapItem[] = useMemo(
    () => filteredItems.map((item: any) => ({
      id: Number(item.servico_id),
      hostId: Number(item.anfitriao?.id || 0) || null,
      price: Number(item.preco_busca ?? item.preco_exibicao ?? item.preco_base ?? 0),
      title: item.anfitriao?.nome || "Anfitrião",
      subtitle: [item.anfitriao?.bairro, item.anfitriao?.cidade].filter(Boolean).join(", "),
      image: resultImage(item),
      rating: Number(item.avaliacoes?.media || 0),
      address: item.anfitriao?.endereco || null,
      neighborhood: item.anfitriao?.bairro || null,
      city: item.anfitriao?.cidade || city || null,
      state: item.anfitriao?.estado || null,
      latitude: item.anfitriao?.latitude ?? null,
      longitude: item.anfitriao?.longitude ?? null,
    })),
    [city, filteredItems]
  );

  const setView = (mode: "list" | "map") => {
    setViewMode(mode);
    const next = new URLSearchParams(searchParams.toString());
    if (mode === "map") next.set("view", "map");
    else next.delete("view");
    setSearchParams(next, { replace: true });
  };

  const applySearch = (value: MarketplaceInlineSearchValue) => {
    const next = new URLSearchParams();
    if (value.service) next.set("service", value.service);
    if (value.city) next.set("cidade", value.city);
    if (value.startDate) next.set("entrada", value.startDate);
    if (value.endDate) next.set("saida", value.endDate);
    next.set("quantidade_pets", value.petCount || "1");
    if (viewMode === "map") next.set("view", "map");
    setSearchParams(next);
  };

  const openAuthModal = () => {
    setAccountMenuOpen(false);

    window.setTimeout(() => {
      const modalElement = document.getElementById("authModal");
      const bootstrapApi = (window as typeof window & {
        bootstrap?: {
          Modal?: {
            getOrCreateInstance: (
              element: HTMLElement,
              options?: Record<string, unknown>,
            ) => { show: () => void };
          };
        };
      }).bootstrap;

      if (!modalElement || !bootstrapApi?.Modal) {
        console.error("Modal de autenticação indisponível.");
        return;
      }

      bootstrapApi.Modal.getOrCreateInstance(modalElement, {
        backdrop: true,
        keyboard: true,
        focus: false,
      }).show();
    }, 0);
  };

  return (
    <div className="min-h-screen bg-[#fffdfa] pb-24 text-slate-950 md:pb-0">
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-3 md:px-6 md:py-4">
          <button type="button" onClick={() => navigate(-1)} className="grid h-10 w-10 shrink-0 place-items-center rounded-full hover:bg-slate-100" aria-label="Voltar">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <a href="/" className="allepets-brand hidden text-3xl leading-none md:block">
            <span className="text-[#FE5668]">Alle</span><span className="text-[#64A002]">Pets</span>
          </a>
          <div className="min-w-0 flex-1">
            <MarketplaceInlineSearchBar
              value={searchValue}
              cities={availableCities.length ? availableCities : city ? [city] : []}
              onSubmit={applySearch}
              compact
              className="mx-auto max-w-4xl"
            />
          </div>
          <div className="relative hidden md:block">
            <button
              type="button"
              aria-label={accountMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-haspopup="menu"
              aria-expanded={accountMenuOpen}
              onClick={() => setAccountMenuOpen((open) => !open)}
              className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#64A002] focus-visible:ring-offset-2"
            >
              <Menu className="h-5 w-5" />
              <UserRound className="h-5 w-5" />
            </button>
            {accountMenuOpen && (
              <div
                role="menu"
                aria-label="Menu da conta"
                className="absolute right-0 top-[calc(100%+0.65rem)] z-50 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white py-2 shadow-xl"
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={openAuthModal}
                  className="block w-full px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Entrar
                </button>
                <a role="menuitem" href="/register" className="block px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">Criar conta</a>
                <div className="my-1 border-t border-slate-100" />
                <a role="menuitem" href="/comecar" className="block px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50">Quero ser anfitrião</a>
              </div>
            )}
          </div>
        </div>

        <div className="mx-auto flex max-w-[1500px] gap-2 overflow-x-auto px-4 pb-3 md:px-6">
          {["Filtros", service === "hospedagem" ? "Hospedagem" : "Serviço", "Até R$ 80", "Responde rápido", "Mais"].map((label, index) => (
            <button key={`${label}-${index}`} type="button" className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${index === 1 ? "border-[#FE5668] bg-[#FE5668]/5 text-[#FE5668]" : "border-slate-200 bg-white text-slate-700"}`}>
              {index === 0 && <SlidersHorizontal className="h-4 w-4" />}
              {label}
              {label === "Mais" && <ChevronDown className="h-4 w-4" />}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] md:px-6 md:py-6">
        {isVeterinario && (
          <section aria-label="Filtros de atendimento veterinário" className="mx-4 mb-5 rounded-2xl border border-slate-200 bg-white p-4 md:mx-0">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div><h2 className="text-lg font-bold">Encontrar atendimento veterinário</h2><p className="text-sm text-slate-600">Pesquise por modalidade e especialidade. Atendimento sujeito à confirmação profissional.</p></div>
              <a className="text-sm font-semibold text-[#4f7f00] underline" href="/vet/minhas-solicitacoes">Acompanhar meus pedidos</a>
            </div>
            <div className="mt-3 flex flex-wrap gap-3">
              <label className="text-sm font-semibold">Modalidade
                <select aria-label="Modalidade veterinária" value={vetModalidade} onChange={(e) => setVetModalidade(e.target.value)} className="mt-1 block rounded-lg border px-3 py-2 font-normal">
                  <option value="todas">Todas</option><option value="atende_domiciliar">Em casa</option><option value="atende_consultorio">Consultório</option><option value="teleconsulta">Online</option>
                </select>
              </label>
              <label className="text-sm font-semibold">Especialidade
                <select aria-label="Especialidade veterinária" value={vetEspecialidade} onChange={(e) => setVetEspecialidade(e.target.value)} className="mt-1 block max-w-[280px] rounded-lg border px-3 py-2 font-normal">
                  <option value="">Todas as especialidades</option>{vetEspecialidades.map((nome) => <option key={nome} value={nome}>{nome}</option>)}
                </select>
              </label>
            </div>
            <p className="mt-3 text-xs text-slate-500">Casos graves (dificuldade para respirar, convulsões ou sangramento intenso) exigem hospital veterinário de urgência. A busca não assegura atendimento imediato.</p>
          </section>
        )}
        <div className="mb-4 flex items-center justify-between px-4 md:px-0">
          <div>
            <h1 className="text-xl font-extrabold md:text-2xl">
              {loading ? "Buscando profissionais..." : isVeterinario ? `${filteredItems.length} veterinário${filteredItems.length === 1 ? "" : "s"} encontrado${filteredItems.length === 1 ? "" : "s"}` : `${total} anfitrião${total === 1 ? "" : "ões"} encontrado${total === 1 ? "" : "s"}`}
            </h1>
            <p className="mt-1 text-sm text-slate-500">Localização exibida de forma aproximada para preservar a privacidade.</p>
          </div>
          <div className="hidden rounded-full border border-slate-200 bg-white p-1 md:flex">
            <button type="button" onClick={() => setView("list")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${viewMode === "list" ? "bg-slate-950 text-white" : "text-slate-600"}`}>
              <List className="h-4 w-4" /> Lista
            </button>
            <button type="button" onClick={() => setView("map")} className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold ${viewMode === "map" ? "bg-slate-950 text-white" : "text-slate-600"}`}>
              <Map className="h-4 w-4" /> Mapa
            </button>
          </div>
        </div>

        {err && <div className="mx-4 mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 md:mx-0">Erro ao carregar os resultados: {err}</div>}

        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(420px,0.95fr)]">
          <section className={`${viewMode === "map" ? "hidden lg:block" : "block"} px-4 md:px-0`}>
            <div className="grid gap-5 sm:grid-cols-2">
              {!loading && isVeterinario && filteredItems.length === 0 && <p className="col-span-full rounded-2xl border p-5 text-sm">Nenhum veterinário encontrado com estes filtros. Experimente outra modalidade, especialidade ou cidade.</p>}
              {listings.map((item: any) => {
                const price = Number(item.preco_exibicao ?? item.preco_base ?? 0);
                const searchPrice = Number(item.preco_busca ?? price);
                const hasSearchEstimate = Math.abs(searchPrice - price) >= 0.01;
                const pricingUnitLabel = item.pricing_unit_label || "";
                const pricingPricePrefix = item.pricing_price_prefix || "";
                const bathGroomingPrices = item.exige_modalidade
                  ? BATH_GROOMING_SEARCH_OPTIONS
                      .map((option) => ({
                        ...option,
                        price: Number(item.modalidades_servico?.[option.key] || 0),
                      }))
                      .filter((option) => option.price > 0)
                  : [];
                const displaysBathGroomingPrices = bathGroomingPrices.length > 0;
                const pricingOptionsCount = Number(item.pricing_options_count || 0);
                const packagesCount = Number(item.packages_count || 0);
                const appliedModifiers = Array.isArray(item.pricing_applied_modifiers) ? item.pricing_applied_modifiers : [];
                const rating = Number(item.avaliacoes?.media || 0);
                const reviews = Number(item.avaliacoes?.total || 0);
                const name = item.anfitriao?.nome || "Anfitrião";
                const location = [item.anfitriao?.bairro, item.anfitriao?.cidade].filter(Boolean).join(", ");

                return (
                  <article
                    key={item.servico_id}
                    onMouseEnter={() => setSelectedMapId(Number(item.servico_id))}
                    className="group cursor-pointer"
                    onClick={() => navigate(`/servico/${item.servico_id}?${searchParams.toString()}`)}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden rounded-[24px] bg-slate-100">
                      <img src={resultImage(item)} alt={`Espaço de ${name}`} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
                      <button type="button" onClick={(event) => event.stopPropagation()} className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-white/90 shadow-sm" aria-label="Favoritar">
                        <Heart className="h-5 w-5" />
                      </button>
                      {rating >= 4.8 && <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1.5 text-xs font-bold shadow-sm">Preferido dos tutores</span>}
                    </div>
                    <div className="mt-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h2 className="truncate font-bold">{name}</h2>
                          <p className="truncate text-sm text-slate-500">{location}</p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1 text-sm">
                          <Star className="h-4 w-4 fill-slate-900" />
                          {reviews > 0 ? `${rating.toFixed(1)} (${reviews})` : "Novo"}
                        </div>
                      </div>
                      <p className="mt-1 truncate text-sm text-slate-500">{item.tipo_servico?.nome || "Serviço pet"} · {isVeterinario ? "Horário sujeito à confirmação" : "Responde rápido"}</p>
                      {displaysBathGroomingPrices ? (
                        <div className="mt-3 space-y-1.5 rounded-xl bg-slate-50 px-3 py-2.5">
                          {bathGroomingPrices.map((option) => (
                            <div key={option.key} className="flex items-center justify-between gap-3 text-sm">
                              <span className="text-slate-600">{option.label}</span>
                              <strong className="whitespace-nowrap text-slate-900">{money(option.price)}</strong>
                            </div>
                          ))}
                          <p className="border-t border-slate-200 pt-1.5 text-xs text-slate-500">Escolha o serviço para reservar</p>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm">{pricingPricePrefix && <span className="text-slate-500">{pricingPricePrefix} </span>}<strong className="text-base">{money(price)}</strong>{pricingUnitLabel && <span className="text-slate-500"> {pricingUnitLabel}</span>}</p>
                      )}
                      {!displaysBathGroomingPrices && hasSearchEstimate && <p className="mt-1 text-sm font-semibold text-[#4f7f00]">Estimativa para esta busca: {money(searchPrice)}</p>}
                      {appliedModifiers.length > 0 && <p className="mt-1 truncate text-xs text-slate-500">Inclui: {appliedModifiers.join(" · ")}</p>}
                      {packagesCount > 0 && <span className="mt-2 inline-flex rounded-full bg-[#eef7df] px-2.5 py-1 text-xs font-bold text-[#4f7f00]">Pacotes disponíveis</span>}
                      {pricingOptionsCount > 0 && <p className="mt-1 text-xs text-slate-500">{pricingOptionsCount} opção{pricingOptionsCount === 1 ? "" : "ões"} configurada{pricingOptionsCount === 1 ? "" : "s"} para a reserva</p>}
                      {isVeterinario && (item.veterinario?.atende_domiciliar || item.veterinario?.atende_consultorio || item.veterinario?.teleconsulta) && (
                        <button type="button" onClick={(event) => { event.stopPropagation(); setVetRequestItem(item as VetListing); }} className="mt-3 w-full rounded-xl bg-[#64A002] px-4 py-3 text-sm font-bold text-white hover:bg-[#4f7f00]">
                          Solicitar atendimento
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
            {!loading && visibleCount < filteredItems.length && (
              <button type="button" onClick={() => setVisibleCount((value) => value + 12)} className="mx-auto mt-8 block rounded-full border border-slate-900 px-6 py-3 text-sm font-bold">
                Mostrar mais
              </button>
            )}
          </section>

          <section className={`${viewMode === "list" ? "hidden lg:block" : "block"} sticky top-[170px] h-[calc(100vh-195px)] px-0 lg:px-0`}>
            <MarketplaceMapCanvas items={mapItems} selectedId={selectedMapId} onSelect={setSelectedMapId} city={city} className="h-full rounded-none lg:rounded-[28px]" />
          </section>
        </div>
      </main>

      <VetQuickRequestDialog listing={vetRequestItem} cidade={city} data={start} onClose={() => setVetRequestItem(null)} />

      <button type="button" onClick={() => setView(viewMode === "list" ? "map" : "list")} className="fixed bottom-24 left-1/2 z-40 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-white shadow-xl lg:hidden">
        {viewMode === "list" ? <Map className="h-4 w-4" /> : <List className="h-4 w-4" />}
        {viewMode === "list" ? "Mostrar mapa" : "Mostrar lista"}
      </button>

      <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 border-t border-slate-200 bg-white px-2 pb-[max(env(safe-area-inset-bottom),8px)] pt-2 md:hidden">
        {[[Search, "Explorar", "/"], [Heart, "Favoritos", "/favoritos"], [CalendarDays, "Reservas", "/tutor/reservas"], [MessageCircle, "Mensagens", "/chat"], [UserRound, "Perfil", "/home"]].map(([Icon, label, href]: any) => (
          <a key={label} href={href} className={`flex flex-col items-center gap-1 py-1 text-[11px] font-medium ${label === "Explorar" ? "text-[#FE5668]" : "text-slate-500"}`}>
            <Icon className="h-5 w-5" />{label}
          </a>
        ))}
      </nav>
    </div>
  );
}
